import React, { useMemo, useState } from "react";

import {
  formatFractionDisplay,
  queryIsLoading,
  useDairyMethodology,
  useGetDairyCowTypes,
  useGetDairyFeedAdditives,
  useGetDairyFeedTypes,
  useGetDairyManureStorage,
  useGetFootprintingDairyFeedInformationByPrimaryDataId,
  useGetFootprintingDairyFeedProductions,
  useGetManureAdditives,
  useTranslation,
} from "utils";

import { getUnitDisplayLabel } from "consts";

import { DAIRY_PRACTICE_SLUG, DAIRY_REFRIGERANTS_ACTIVE, DairyFootprintReturnBody, FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE } from "models";

import { Card, ListCard } from "components";

import { FootprintingDairyFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingDairyFeedProductionDrawer";
import { refrigerantUsageDataPairs } from "pages/app/footprinting/components/FootprintingDairyReview";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import DairyRefrigerantsForm from "pages/app/footprinting/components/formFlows/dairyFlow/DairyRefrigerantsForm";
import { FeedAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedAdditiveForm";
import { FeedForageLossForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedForageLossForm";
import { FeedInformationForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedInformationForm";
import { FeedPastureForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedPastureForm";
import { CommercialClassesForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdCommercialClasses";
import { HerdConditionForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdConditionForm";
import { HerdDetailsForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdDetailsForm";
import { HerdOffspringForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdOffspringForm";
import { HerdSizeForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdSizeForm";
import { ManureAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureAdditiveForm";
import { ManureStorageForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureStorageForm";
import { MilkYieldForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MilkYieldForm";
import { MoorlandForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MoorlandForm";
import { DairyCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FootprintEditDairyDetailsCardProps {
  dairyFootprintData: DairyFootprintReturnBody;
  isDairyFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setDairyModalForm: (formType: string) => void;
  farmId: string;
  year: number;
}

const FootprintEditDairyDetailsCard = ({
  dairyFootprintData,
  isDairyFootprintDataLoading,
  setShowEditModal,
  setDairyModalForm,
  farmId,
  year,
}: FootprintEditDairyDetailsCardProps) => {
  const { t } = useTranslation(["footprintingDairy", "footprinting", "dairy", "practices"]);

  const cowTypesQuery = useGetDairyCowTypes({ enabled: true });
  const feedTypesQuery = useGetDairyFeedTypes();
  const feedAdditivesQuery = useGetDairyFeedAdditives();
  const manureStorageQuery = useGetDairyManureStorage();
  const manureAdditivesQuery = useGetManureAdditives();
  const { isCfp } = useDairyMethodology(farmId);

  const { footprint } = dairyFootprintData;

  const feedInformationQuery = useGetFootprintingDairyFeedInformationByPrimaryDataId(footprint?.id ?? "");
  const selfProducedFeeds = useMemo(() => (feedInformationQuery.data ?? []).filter((feedInfo) => feedInfo.isSelfProduced), [feedInformationQuery.data]);
  const feedProductionQueries = useGetFootprintingDairyFeedProductions(selfProducedFeeds.map((feedInfo) => feedInfo.id));

  const [selectedFeedInformationId, setSelectedFeedInformationId] = useState<string | null>(null);

  // Verified footprints are read-only: the admin unverify is the only write the API accepts
  const editButton = (formKey: string) =>
    footprint?.isVerified
      ? undefined
      : {
          label: t("footprinting:review.edit"),
          onClick: () => {
            setShowEditModal(true);
            setDairyModalForm(formKey);
          },
        };

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`, { ns: "footprinting" }));
  const isLoading =
    isDairyFootprintDataLoading ||
    [cowTypesQuery, feedTypesQuery, feedAdditivesQuery, manureStorageQuery, manureAdditivesQuery, feedInformationQuery].some(queryIsLoading) ||
    feedProductionQueries.some(queryIsLoading);

  return (
    <Card className="admin-edit-dairy-details-card padding-125">
      <ReviewPageTitle title={t("review.dairy.title")} iconName="klim/cow" isLoading={isLoading} />
      {/* Milk yield */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.milkYield.cardTitle")}
        titleButton={editButton(MilkYieldForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.milkYield.milkYield"),
            value: `${footprint?.milkYieldKgPerCow}`,
          },
          {
            label: t("reviewAndSubmit.milkYield.deliveredOrProduced"),
            value: footprint?.milkProduction === "produced" ? "produced" : "delivered",
          },
          {
            label: t("reviewAndSubmit.milkYield.fatContent"),
            value: `${formatFractionDisplay(footprint?.fatContentPct ?? 0)} %`,
          },
          {
            label: t("reviewAndSubmit.milkYield.proteinContent"),
            value: `${formatFractionDisplay(footprint?.proteinContentPct ?? 0)} %`,
          },
        ]}
      />
      {/* Herd size */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdSize.cardTitle")}
        titleButton={editButton(HerdSizeForm.Key)}
        dataPairs={[
          ...(footprint?.herdSize
            .filter(({ cowCount }) => (cowCount ?? 0) > 0)
            .map((herdSizeInfo) => ({
              label: cowTypesQuery.data?.find((ct) => ct.id === herdSizeInfo.cowTypeId)?.name ?? "",
              value: `${herdSizeInfo.cowCount}`,
            })) ?? []),
          {
            label: t("reviewAndSubmit.herdSize.calves"),
            value: `${footprint?.herdDetails.calvesAmount}`,
          },
        ]}
      />
      {/* Herd details */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdDetails.cardTitle")}
        titleButton={editButton(HerdDetailsForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.herdDetails.cowLosses"),
            value: `${footprint?.herdDetails.avgCowLosses}`,
          },
          {
            label: t("reviewAndSubmit.herdDetails.usefulLifespan"),
            value: `${footprint?.herdDetails.avgCowUsefulLifeMonths} ${t("dairyHerdDetailsForm.question.avgCowUsefulLifeMonthsSuffix")}`,
          },
        ]}
      />
      {/* Weight / Condition */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.wightCondition.cardTitle")}
        titleButton={editButton(HerdConditionForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.wightCondition.usedEstimation"),
            value: displayRadioAnswer(footprint?.herdDetails.cowWeightKnown === "no"),
          },
          {
            label: t("dairyHerdConditionForm.question.cowWeightInput"),
            value: `${footprint?.herdDetails.averageCowWeightKg} ${t("dairyHerdConditionForm.question.cowWeightSuffix")}`,
          },
        ]}
      />
      {/* The Offspring */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.offspring.cardTitle")}
        titleButton={editButton(HerdOffspringForm.Key)}
        dataPairs={[
          {
            label: t("dairyHerdOffspringForm.question.matFeedPctInput"),
            value: `${formatFractionDisplay(footprint?.herdDetails.matFeedPct ?? 0)} %`,
          },
          {
            label: t("dairyHerdOffspringForm.question.wholeMilkFeedPctInput"),
            value: `${formatFractionDisplay(footprint?.herdDetails.wholeMilkFeedPct ?? 0)} %`,
          },
          {
            label: t("dairyHerdOffspringForm.question.ageOfFirstCalvingInput"),
            value: `${footprint?.herdDetails.ageOfFirstCalvingMonths} ${t("dairyHerdOffspringForm.question.ageOfFirstCalvingSuffix")}`,
          },
          {
            label: t("dairyHerdOffspringForm.question.timeBetweenCalvingInput"),
            value: `${footprint?.herdDetails.timeBetweenCalvingDays} ${t("dairyHerdOffspringForm.question.timeBetweenCalvingSuffix")}`,
          },
        ]}
      />
      {/* Cow sales */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.cowSales.cardTitle")}
        titleButton={editButton(CommercialClassesForm.Key)}
        dataPairs={[
          {
            label: t("dairyHerdCommercialClassesForm.question.poorConditionInput"),
            value: `${footprint?.cowSales.cowSalesInPoorCondition}`,
          },
          {
            label: t("dairyHerdCommercialClassesForm.question.lightManufacturingInput"),
            value: `${footprint?.cowSales.cowSalesForLightManufacturing}`,
          },
          {
            label: t("dairyHerdCommercialClassesForm.question.standardManufacturingInput"),
            value: `${footprint?.cowSales.cowSalesForStandardManufacturing}`,
          },
          {
            label: t("dairyHerdCommercialClassesForm.question.goodConditionInput"),
            value: `${footprint?.cowSales.cowSalesInGoodCondition}`,
          },
          {
            label: t("dairyHerdCommercialClassesForm.question.premiumConditionInput"),
            value: `${footprint?.cowSales.cowSalesInPremiumCondition}`,
          },
        ]}
      />
      {/* Feed pasture */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedPasture.cardTitle")}
        titleButton={editButton(FeedPastureForm.Key)}
        dataPairs={[
          {
            label: t("dairyFeedPastureForm.cowPastureRadioQuestion"),
            value: t(`dairyFeedPastureForm.cowPastureOptions.${footprint?.cowsInPastureActive}`),
          },
          ...(footprint?.cowsInPastureActive === "yes"
            ? [
                {
                  label: t("dairyFeedPastureForm.question.cowsInPastureInput"),
                  value: ` ${footprint?.cowsInPasture ?? 0} ${t("dairyFeedPastureForm.question.cowsInPastureSuffix")}`,
                },
                {
                  label: t("dairyFeedPastureForm.question.daysInPastureInput"),
                  value: ` ${footprint?.daysInPasturePerYear ?? 0} ${t("dairyFeedPastureForm.question.daysInPastureSuffix")}`,
                },
                {
                  label: t("dairyFeedPastureForm.question.avgHoursInPastureInput"),
                  value: ` ${footprint?.avgHoursInPasturePerDay ?? 0} ${t("dairyFeedPastureForm.question.avgHoursInPastureSuffix")}`,
                },
              ]
            : []),
        ]}
      />
      {/* Feed types / amounts */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedTypes.cardTitle")}
        titleButton={editButton(FeedInformationForm.Key)}
        dataPairs={footprint?.feedInformation.map((feedInfo) => {
          const feedType = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedTypeId);
          const energy = `${feedInfo.energyValue} ${getUnitDisplayLabel(feedInfo.energyUnit)}`;
          return {
            label: feedType?.name ?? "",
            value: feedType?.isCustom
              ? `${energy} · ${t("dairyFeedTypesForm.question.emissionFactor")}: ${feedInfo.emissionFactorKgCo2Eq} ${t(
                  "dairyFeedTypesForm.question.emissionFactorUnit"
                )}`
              : energy,
          };
        })}
      />
      {/* Self-produced feed production */}
      {selfProducedFeeds.map((feedInfo, index) => {
        const feedTypeName = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedTypeId)?.name ?? "";
        const production = feedProductionQueries[index]?.data;
        const isSplitRelevant = production?.cropUsedForFeed === FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO;

        return (
          <ListCard
            key={feedInfo.id}
            className="margin-bottom-100"
            isOutlined
            isLoading={isLoading}
            title={`Self-Produced ${feedTypeName || t("dairyFeedProductionForm.title")}`}
            titleButton={footprint?.isVerified ? undefined : { label: t("footprinting:review.edit"), onClick: () => setSelectedFeedInformationId(feedInfo.id) }}
            dataPairs={[
              {
                label: t("dairyFeedProductionForm.question.cropUsageRadioTitle"),
                value: production?.cropUsedForFeed ? t(`dairyFeedProductionForm.cropUsageOptions.${production.cropUsedForFeed}`) : "",
              },
              { label: t("dairyFeedProductionForm.question.cropYieldTPerHa"), value: `${production?.cropYieldTPerHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.dieselConsumptionLHa"), value: `${production?.dieselConsumptionLHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.energyUsedKwhHa"), value: `${production?.energyUsedKwhHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.mineralNitrogenKgHa"), value: `${production?.mineralNitrogenKgHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.mineralPhosphorusKgHa"), value: `${production?.mineralPhosphorusKgHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.mineralPotassiumKgHa"), value: `${production?.mineralPotassiumKgHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.nitrogenOrganicFertKgHa"), value: `${production?.nitrogenOrganicFertKgHa ?? ""}` },
              { label: t("dairyFeedProductionForm.question.totalLimeAppliedKgCaCo3PerHa"), value: `${production?.totalLimeAppliedKgCaCo3PerHa ?? ""}` },
              {
                label: t("dairyFeedProductionForm.question.totalPlantProtectionApplied"),
                value: `${production?.totalPlantProtectionApplied ?? ""} ${production?.totalPlantProtectionAppliedUnit ?? ""}`.trim(),
              },
              { label: t("dairyFeedProductionForm.question.totalWaterUsedIrregationLPerHa"), value: `${production?.totalWaterUsedIrregationLPerHa ?? ""}` },
              ...(isSplitRelevant
                ? [
                    { label: t("dairyFeedProductionForm.question.soldOffFarmPct"), value: `${formatFractionDisplay(production?.soldOffFarmPct ?? 0)} %` },
                    { label: t("dairyFeedProductionForm.question.fedToCowsPct"), value: `${formatFractionDisplay(production?.fedToCowsPct ?? 0)} %` },
                    { label: t("dairyFeedProductionForm.question.otherUsesPct"), value: `${formatFractionDisplay(production?.otherUsesPct ?? 0)} %` },
                  ]
                : []),
            ]}
          />
        );
      })}
      {/* Feed additives */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedAdditives.cardTitle")}
        titleButton={editButton(FeedAdditiveForm.Key)}
        dataPairs={[
          {
            label: t("dairyFeedAdditivesForm.feedAdditivesQuestion"),
            value: t(`dairyFeedAdditivesForm.feedAdditiveOptions.${footprint?.feedAdditiveActive}`),
          },
          ...(footprint?.feedAdditiveUsage ?? []).map((feedAdditiveUsage) => ({
            label: t(
              `${DAIRY_PRACTICE_SLUG.FEED_ADDITIVES}.${feedAdditivesQuery.data?.find((fa) => fa.id === feedAdditiveUsage.additiveId)?.translationSlug}`,
              {
                ns: "dairy",
              }
            ),
            value: `${feedAdditiveUsage.amount} ${feedAdditiveUsage.amountUnit}`,
          })),
        ]}
      />
      {/* Forage loss */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.forageLoss.cardTitle")}
        titleButton={editButton(FeedForageLossForm.Key)}
        dataPairs={[
          {
            label: t("dairyFeedForageLossForm.forageLossQuestion"),
            value: `${formatFractionDisplay(footprint?.forageLossPct ?? null)} %`,
          },
        ]}
      />
      {/* Manure storage */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureStorage.cardTitle")}
        titleButton={editButton(ManureStorageForm.Key)}
        dataPairs={[
          ...(footprint?.manureStorageUsage ?? []).map(
            (manureStorageUsage) => ({
              label: manureStorageQuery.data?.find(({ id }) => id === manureStorageUsage.storageId)?.name ?? "Unknown",
              value: `${formatFractionDisplay(manureStorageUsage.amountStoredPct)} %`,
            }),
            {
              label: "Gas tank storage",
              value: `${formatFractionDisplay(footprint?.amountStoredGasTankPct ?? 0)} %`,
            }
          ),
        ]}
      />
      {/* Manure additives */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureAdditives.cardTitle")}
        titleButton={editButton(ManureAdditiveForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.manureAdditives.sluryAcidifiersUsed"),
            value: displayRadioAnswer((footprint?.manureAdditiveUsage.length ?? 0) > 0),
          },
          ...(footprint?.manureAdditiveUsage ?? []).map((manureAdditiveUsage) => ({
            label: manureAdditivesQuery.data?.find(({ id }) => manureAdditiveUsage.additiveId === id)?.name ?? "",
            value: `${manureAdditiveUsage.amountUsedM3}`,
          })),
        ]}
      />
      {/* Refrigerants (CFP only: LfL has no refrigerant inputs) */}
      {isCfp && (
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("footprinting:navigation.dairy.refrigeration")}
          titleButton={editButton(DairyRefrigerantsForm.Key)}
          dataPairs={[
            {
              label: t("dairyRefrigerantsForm.question.refrigerantsUsed"),
              value: displayRadioAnswer(footprint?.refrigerantsActive ? footprint.refrigerantsActive === DAIRY_REFRIGERANTS_ACTIVE.YES : null),
              children: (footprint?.refrigerantUsage ?? []).map((usage, index) => (
                <ListCard
                  key={usage.id}
                  isSublist
                  isLoading={isLoading}
                  title={t("dairyRefrigerantsForm.question.title") + (index + 1)}
                  dataPairs={refrigerantUsageDataPairs(t, usage)}
                />
              )),
            },
          ]}
        />
      )}
      {/* Moorland */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.moorland.cardTitle")}
        titleButton={editButton(MoorlandForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.moorland.moorlandUsed"),
            value: displayRadioAnswer(footprint?.moorlandActive === "yes"),
          },
          ...(footprint?.moorlandActive === "yes"
            ? [
                {
                  label: t("reviewAndSubmit.moorland.fodderArea"),
                  value: `${footprint.fodderProductionAreaHa} ${t("dairyMoorlandForm.question.fodderProductionAreaSuffix")}`,
                },
                {
                  label: t("reviewAndSubmit.moorland.averageWaterLevel"),
                  value: `${footprint.annualWaterLevelCm} ${t("dairyMoorlandForm.question.annualWaterLevelSuffix")}`,
                },
              ]
            : []),
        ]}
      />
      {/* Comment */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("commentForm.title", { ns: "footprinting" })}
        titleButton={editButton(DairyCommentForm.Key)}
        dataPairs={[
          {
            label: t("commentForm.label", { ns: "footprinting" }),
            value: footprint?.comment || t("commentForm.noComment"),
          },
        ]}
      />
      {selectedFeedInformationId && (
        <FootprintingDairyFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedInformationId !== null}
          onClose={() => setSelectedFeedInformationId(null)}
          feedInformationId={selectedFeedInformationId}
          primaryDataId={footprint?.id}
          feedTypeName={
            feedTypesQuery.data?.find(({ id }) => id === selfProducedFeeds.find((feedInfo) => feedInfo.id === selectedFeedInformationId)?.feedTypeId)?.name
          }
        />
      )}
    </Card>
  );
};

export default FootprintEditDairyDetailsCard;
